import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/client';
import PageMeta from '../components/PageMeta';
import PasswordInput from '../components/PasswordInput';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validating, setValidating] = useState(true);
  const [tokenError, setTokenError] = useState('');

  useEffect(() => {
    if (!token) { setValidating(false); return; }
    api.get(`/auth/reset-password?token=${encodeURIComponent(token)}`)
      .then(() => setValidating(false))
      .catch(err => {
        setTokenError(err.response?.data?.error || 'This reset link is invalid or has expired.');
        setValidating(false);
      });
  }, [token]);

  function validatePassword(pw) {
    if (pw.length < 8) return 'Password must be at least 8 characters';
    if (!/[A-Z]/.test(pw)) return 'Password must contain at least one uppercase letter';
    if (!/[0-9]/.test(pw)) return 'Password must contain at least one digit';
    if (!/[^A-Za-z0-9]/.test(pw)) return 'Password must contain at least one special character';
    return null;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!token) { setError('Invalid reset link. Request a new one.'); return; }
    const pwError = validatePassword(password);
    if (pwError) { setError(pwError); return; }
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    setError('');
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      setDone(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Reset failed. The link may have expired.');
    } finally {
      setLoading(false);
    }
  }

  if (!token) {
    return (
      <div className="auth-page">
        <PageMeta title="Reset Password" description="Choose a new password for your Agence account." path="/reset-password" />
        <div className="auth-card">
          <h1>Agence</h1>
          <h2>Invalid link</h2>
          <p style={{ color: 'var(--text-muted)', marginBottom: '1rem' }}>This reset link is missing or malformed.</p>
          <Link to="/forgot-password">Request a new link</Link>
        </div>
      </div>
    );
  }

  if (validating) {
    return (
      <div className="auth-page">
        <PageMeta title="Reset Password" description="Choose a new password for your Agence account." path="/reset-password" />
        <div className="auth-card">
          <h1>Agence</h1>
          <h2>Checking link…</h2>
        </div>
      </div>
    );
  }

  if (tokenError) {
    return (
      <div className="auth-page">
        <PageMeta title="Reset Password" description="Choose a new password for your Agence account." path="/reset-password" />
        <div className="auth-card">
          <h1>Agence</h1>
          <h2>Link expired</h2>
          <p style={{ color: 'var(--text-muted)', marginBottom: '1rem' }}>{tokenError}</p>
          <Link to="/forgot-password">Request a new link</Link>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="auth-page">
        <PageMeta title="Reset Password" description="Choose a new password for your Agence account." path="/reset-password" />
        <div className="auth-card">
          <h1>Agence</h1>
          <h2>Password updated</h2>
          <p style={{ color: 'var(--text-muted)', marginBottom: '1rem' }}>
            Your password has been reset. Redirecting to sign in…
          </p>
          <Link to="/login">Sign in now</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <PageMeta title="Reset Password" description="Choose a new password for your Agence account." path="/reset-password" />
      <div className="auth-card">
        <h1>Agence</h1>
        <h2>Choose a new password</h2>
        <form onSubmit={handleSubmit}>
          <PasswordInput
            aria-label="New password (8 or more characters, including uppercase, number, and symbol)"
            placeholder="New password (8+ characters)"
            autoComplete="new-password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            minLength={8}
            required
          />
          <p style={{ margin: '-0.4rem 0 0', fontSize: '0.78rem', color: 'var(--text-muted)', textAlign: 'left' }}>
            Must include an uppercase letter, a number, and a special character.
          </p>
          <PasswordInput
            aria-label="Confirm new password"
            placeholder="Confirm new password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            required
          />
          {error && <p className="error">{error}</p>}
          <button type="submit" disabled={loading || !password}>
            {loading ? 'Updating…' : 'Update password'}
          </button>
        </form>
        <p style={{ marginTop: '0.75rem' }}><Link to="/login">Back to sign in</Link></p>
      </div>
    </div>
  );
}
